import React, { useState, useEffect } from 'react';
import { Eye, Trash2, Plus, Search, Filter } from 'lucide-react';
import JobViewModal from './JobViewModal';
import './JobList.scss';

interface Job {
  id: number;
  title: string;
  description: string;
  short_description?: string;
  location: string;
  job_type: 'full_time' | 'part_time' | 'contract' | 'internship' | 'temporary';
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  status: 'draft' | 'pending' | 'published' | 'closed' | 'archived' | 'declined';
  views_count?: number;
  applications_count?: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  company_name?: string;
  employer_email?: string;
}

const JobList: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const itemsPerPage = 20;

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const loadJobs = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        throw new Error('No admin token found');
      }

      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: ((currentPage - 1) * itemsPerPage).toString(),
        ...(statusFilter !== 'all' && { status: statusFilter })
      });

      const response = await fetch(`/api/jobs/admin/all?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch jobs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setJobs(data.data.jobs || []);
        // Note: In a real implementation, you'd get total count from the API
        setTotalJobs(data.data.jobs?.length || 0);
      } else {
        throw new Error(data.message || 'Failed to load jobs');
      }
    } catch (error: any) {
      console.error('Error loading jobs:', error);
      setError(error.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId: number, jobTitle: string) => {
    if (!window.confirm(`Ar tikrai norite ištrinti darbo skelbimą "${jobTitle}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        throw new Error('No admin token found');
      }

      const response = await fetch(`/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete job');
      }

      const data = await response.json();
      
      if (data.success) {
        // Remove the deleted job from the list
        setJobs(jobs.filter(job => job.id !== jobId));
        setTotalJobs(prev => prev - 1);
      } else {
        throw new Error(data.message || 'Failed to delete job');
      }
    } catch (error: any) {
      console.error('Error deleting job:', error);
      alert(error.message || 'Failed to delete job');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const formatJobType = (jobType: string) => {
    const jobTypeMap: { [key: string]: string } = {
      'full_time': 'Pastovus',
      'part_time': 'Dalinis etatas',
      'contract': 'Kontraktinis',
      'internship': 'Praktika',
      'temporary': 'Laikinas'
    };
    return jobTypeMap[jobType] || jobType;
  };

  const formatStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'draft': 'Juodraštis',
      'pending': 'Laukia patvirtinimo',
      'published': 'Publikuotas',
      'closed': 'Uždarytas',
      'archived': 'Archyvuotas',
      'declined': 'Atmestas',
    };
    return statusMap[status] || status;
  };

  const formatSalary = (salaryMin?: number, salaryMax?: number, currency: string = 'EUR') => {
    if (!salaryMin && !salaryMax) {
      return 'Derinama';
    }
    
    const currencySymbol = currency === 'EUR' ? '€' : currency;
    
    if (salaryMin && salaryMax) {
      return `${salaryMin} - ${salaryMax} ${currencySymbol}`;
    } else if (salaryMin) {
      return `nuo ${salaryMin} ${currencySymbol}`;
    } else if (salaryMax) {
      return `iki ${salaryMax} ${currencySymbol}`;
    }
    
    return 'Derinama';
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'published':
        return 'status-badge status-badge--published';
      case 'draft':
        return 'status-badge status-badge--draft';
      case 'pending':
        return 'status-badge status-badge--pending';
      case 'closed':
        return 'status-badge status-badge--closed';
      case 'archived':
        return 'status-badge status-badge--archived';
      case 'declined':
        return 'status-badge status-badge--declined';
      default:
        return 'status-badge';
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      job.title.toLowerCase().includes(searchLower) ||
      job.company_name?.toLowerCase().includes(searchLower) ||
      job.location.toLowerCase().includes(searchLower) ||
      job.employer_email?.toLowerCase().includes(searchLower)
    );
  });

  const totalPages = Math.ceil(totalJobs / itemsPerPage);

  const handleViewJob = (job: Job) => {
    setSelectedJob(job);
    setShowJobModal(true);
  };

  const handleCloseJobModal = () => {
    setSelectedJob(null);
    setShowJobModal(false);
  };

  return (
    <div className="job-list">
      <div className="job-list__header">
        <h1>Darbo skelbimai</h1>
        <div className="header-actions">
          <button 
            className="create-btn"
            onClick={() => alert('Admin job creation - coming soon!')}
          >
            <Plus size={16} />
            Naujas skelbimas
          </button>
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Ieškoti skelbimų..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <Filter size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Visi</option>
              <option value="draft">Juodraščiai</option>
              <option value="pending">Laukia patvirtinimo</option>
              <option value="published">Publikuoti</option>
              <option value="declined">Atmesti</option>
              <option value="closed">Uždaryti</option>
              <option value="archived">Archyvuoti</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner">Kraunama...</div>
        </div>
      ) : (
        <>
          <div className="job-table-container">
            <table className="job-table">
              <thead>
                <tr>
                  <th>Pavadinimas</th>
                  <th>Įmonė</th>
                  <th>Vieta</th>
                  <th>Tipas</th>
                  <th>Atlyginimas</th>
                  <th>Statusas</th>
                  <th>Peržiūros</th>
                  <th>Sukurta</th>
                  <th>Veiksmai</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="no-data">
                      {searchTerm ? 'Nerasta skelbimų pagal paieškos kriterijus' : 'Nėra darbo skelbimų'}
                    </td>
                  </tr>
                ) : (
                  filteredJobs.map((job) => (
                    <tr key={job.id}>
                      <td className="job-title">
                        <div className="title-container">
                          <strong>{job.title}</strong>
                          {job.short_description && (
                            <small>{job.short_description.substring(0, 100)}...</small>
                          )}
                        </div>
                      </td>
                      <td>{job.company_name || 'N/A'}</td>
                      <td>{job.location}</td>
                      <td>{formatJobType(job.job_type)}</td>
                      <td>{formatSalary(job.salary_min, job.salary_max, job.salary_currency)}</td>
                      <td>
                        <span className={getStatusClass(job.status)}>
                          {formatStatus(job.status)}
                        </span>
                      </td>
                      <td>{job.views_count || 0}</td>
                      <td>{formatDate(job.created_at)}</td>
                      <td className="actions">
                        <button
                          className="action-btn action-btn--view"
                          title="Peržiūrėti"
                          onClick={() => handleViewJob(job)}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="action-btn action-btn--delete"
                          title="Ištrinti"
                          onClick={() => handleDeleteJob(job.id, job.title)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Ankstesnis
              </button>
              
              <span className="pagination-info">
                Puslapis {currentPage} iš {totalPages}
              </span>
              
              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Kitas
              </button>
            </div>
          )}
        </>
      )}

      {/* Job View Modal */}
      {selectedJob && (
        <JobViewModal
          job={selectedJob}
          isOpen={showJobModal}
          onClose={handleCloseJobModal}
        />
      )}
    </div>
  );
};

export default JobList;
